/* v68: self-contained progressive enhancement. No cart, price or form integrations. */
(() => {
  'use strict';
  if (customElements.get('yds-emil-editorial')) return;

  class YdsEmilEditorial extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.dialog = this.querySelector('.yds-emil__dialog');
      this.dialogBody = this.querySelector('[data-yds-dialog-body]');
      this.dialogTitle = this.querySelector('[data-yds-dialog-title]');
      this.returnFocus = null;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.addEventListener('click', (event) => {
        // Preserve modified clicks (new tab, download, etc.) and normal fallback links.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a');
        if (!link || !this.contains(link)) return;

        if (link.matches('[data-yds-artwork], [data-yds-video]')) {
          const isVideo = link.hasAttribute('data-yds-video');
          const videoId = link.dataset.ydsVideo || '';
          if (isVideo) {
            if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return;
            event.preventDefault();
            this.playInlineVideo(link, videoId);
          } else {
            if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
            event.preventDefault();
            this.openMedia(link, false, '');
          }
        } else if (link.getAttribute('href')?.startsWith('#')) {
          const id = link.getAttribute('href').slice(1);
          const target = document.getElementById(id);
          if (!target) return;
          // This is scroll/focus only; input values in the main product are never touched.
          event.preventDefault();
          const header = document.querySelector('.theme__header, [data-header-wrapper]');
          const headerHeight = Math.min(header?.getBoundingClientRect().height || 0, 200);
          const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - 20;
          window.scrollTo({ top: Math.max(0, top), behavior: this.motion.matches ? 'instant' : 'smooth' });
          const heading = target.querySelector('h1, h2, h3') || target;
          const hadTabindex = heading.hasAttribute('tabindex');
          if (!hadTabindex) heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          if (!hadTabindex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
        }
      }, options);

      this.querySelector('[data-yds-dialog-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog?.addEventListener('close', () => this.cleanMedia(), options);
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
    }

    playInlineVideo(link, videoId) {
      const iframe = document.createElement('iframe');
      iframe.className = 'yds-emil__inline-video';
      iframe.title = 'YDS Studio step-by-step mural installation instructions';
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.loading = 'eager';
      link.replaceWith(iframe);
      iframe.focus?.({ preventScroll: true });
    }

    openMedia(link, isVideo, videoId) {
      this.dialogBody.replaceChildren();
      this.returnFocus = link;
      this.dialogTitle.textContent = link.dataset.dialogTitle || 'Take a closer look';
      if (isVideo) {
        const iframe = document.createElement('iframe');
        iframe.title = 'YDS Studio mural installation guide';
        iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        this.dialogBody.appendChild(iframe);
        const paragraph = document.createElement('p');
        paragraph.append('Video not loading? ');
        const fallback = document.createElement('a');
        fallback.href = link.href;
        fallback.target = '_blank';
        fallback.rel = 'noopener';
        fallback.textContent = 'Watch on YouTube';
        fallback.style.textDecoration = 'underline';
        paragraph.appendChild(fallback);
        this.dialogBody.appendChild(paragraph);
      } else {
        const image = document.createElement('img');
        image.src = link.href;
        image.alt = 'Full size mural artwork preview';
        image.decoding = 'async';
        this.dialogBody.appendChild(image);
      }
      this.dialog.showModal();
    }

    cleanMedia() {
      // Removing the iframe stops audio, including when dismissed with Escape.
      this.dialogBody?.replaceChildren();
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanMedia();
      this.abortController?.abort();
      this.abortController = null;
    }
  }
  customElements.define('yds-emil-editorial', YdsEmilEditorial);
})();
